import { sync } from '@genesislcap/foundation-utils';
import { html, repeat, when, ViewTemplate, ref } from '@microsoft/fast-element';
import {
  eventDetail,
  getFormattedDate,
  getFormattedNumber,
  getTenorRangePattern,
} from '../../../utils/formatting';
import { getAbbreviationForCreditProfile } from '../../../utils/credit-profiles'
import {
  listIcon,
  whiteCheckIcon,
  whiteErrorIcon,
  chevronDown,
  chevronUp,
  closeIcon,
} from '../../../styles';
import type { StructuredChatModal } from './structured-chat';
import {
  USED_STRUCTURED_CHAT_STATUS,
  VALID_STRUCTURED_CHAT_STATUS,
} from '../../../services/structured-chat.service';
import { VPPA_RFQ_STATUS } from '../../../utils/enums/generic.enum';

export const StructuredChatModalTemplate: ViewTemplate<StructuredChatModal> = html`
  <right-side-modal
    classes="structured-chat"
    :show="${(x) => x.show}"
    @modalClosing=${(x) => x.closeModal()}
    width="${(x) => x.width}"
    header="${(x) => x.title}"
    buttons="false"
    closeModalHeader="${(x) =>
      x.structuredChatService.allStagedNegotiations.length > 0 ? x.closeModalHeader : ''}"
    closeModalDescription="${(x) =>
      x.structuredChatService.allStagedNegotiations.length > 0 ? x.closeModalDescription : ''}"
  >
    ${when(
      (x) => x.addRFQ,
      html<StructuredChatModal>`
        <add-rfq ${ref('addRfqForm')} @closeAddRfq=${(x, c) => x.closeAddRFQ(eventDetail(c))}></add-rfq>
      `,
    )}
    ${when(
      (x) => !x.addRFQ,
      html<StructuredChatModal>`
        <rfq-selector
          ${ref('rfqSelector')}
          style="display: ${(x) => x.displaySelector}"
          @showAddRfq=${(x, c) => x.showAddRFQ(eventDetail(c))}
          @closeRFQSelector=${(x, c) => x.closeRFQSelector(c)}
        ></rfq-selector>
        ${when(
          (x) =>
            !(
              x.linkMoreRFQs ||
              (x.structuredChatService.selectedVPPAIDs.length &&
                !x.structuredChatService.selectedRFQIDs.length)
            ),
          html<StructuredChatModal>`
            <div class="negotiations-wrapper">
              <div class="negotiations">
                <div class="group-by-header">Group by:</div>
                <zero-segmented-control
                  :value="${sync((x) => x.filterByValue)}"
                  appearance="secondary"
                  orientation="horizontal"
                >
                  <zero-segmented-item value="status">Status</zero-segmented-item>
                  <zero-segmented-item value="project">Project</zero-segmented-item>
                </zero-segmented-control>
                ${when(
                  (x) => x.filterByValue === 'status',
                  html<StructuredChatModal>`
                    <fast-accordion>
                      ${repeat(
                        (x) => x.negotiationStatuses,
                        html`
                          ${when(
                            (x) =>
                              Object.values(VALID_STRUCTURED_CHAT_STATUS)?.includes(x.ENUM_VALUE),
                            html`
                              <fast-accordion-item
                                expanded
                                style="${(x, c) =>
                                  c.parent.structuredChatService.allStagedNegotiations.length ===
                                    0 && x.ENUM_VALUE === VALID_STRUCTURED_CHAT_STATUS.STAGED
                                    ? 'display: none'
                                    : ''}"
                              >
                                <div slot="heading">${(x) => x.READABLE_VALUE}</div>
                                ${repeat(
                                  (x, c) => c.parent.structuredChatService.combinedNegotiations,
                                  html`
                                    ${when(
                                      (x, c) =>
                                        c.parentContext.parent.structuredChatService.validStatusMap
                                          .get(c.parent.ENUM_VALUE)
                                          ?.includes(x.NEGOTIATION_STATUS),
                                      html`
                                        <negotiation-tile
                                          :negotiation=${(x) => x}
                                          @resetForm="${(x, c) =>
                                            c.parentContext.parent.resetResponseForm()}"
                                        ></negotiation-tile>
                                      `,
                                    )}
                                  `,
                                )}
                                ${chevronUp('expanded-icon')} ${chevronDown('collapsed-icon')}
                              </fast-accordion-item>
                            `,
                          )}
                        `,
                      )}
                    </fast-accordion>
                  `,
                )}
                ${when(
                  (x) => x.filterByValue === 'project',
                  html`
                    <fast-accordion>
                      ${repeat(
                        (x) => x.structuredChatService.uniqueProjects,
                        html`
                          <fast-accordion-item expanded>
                            <div slot="heading">${(x) => x}</div>
                            ${repeat(
                              (x, c) => c.parent.structuredChatService.combinedNegotiations,
                              html`
                                ${when(
                                  (x, c) => x.PROJECT_NAME === c.parent,
                                  html`
                                    <negotiation-tile
                                      :negotiation=${(x) => x}
                                      @resetForm="${(x, c) =>
                                        c.parentContext.parent.resetResponseForm()}"
                                    ></negotiation-tile>
                                  `,
                                )}
                              `,
                            )}
                            ${chevronUp('expanded-icon')} ${chevronDown('collapsed-icon')}
                          </fast-accordion-item>
                        `,
                      )}
                    </fast-accordion>
                  `,
                )}
              </div>
              ${when(
                (x) =>
                  !x.structuredChatService.allNegotiations.length &&
                  !x.structuredChatService.negotiationInViewDetails.VPPA_UNIQUE_ID,
                html`
                  <div class="proposals-responses-wrapper">
                    <div class="no-data-message">
                      <h3>Engagements not Found</h3>
                      <p>There are no active Engagements at this time</p>
                    </div>
                  </div>
                `,
              )}
              ${when(
                (x) =>
                  x.structuredChatService.allNegotiations.length ||
                  x.structuredChatService.negotiationInViewDetails.VPPA_UNIQUE_ID,
                html`
              <div class="proposals-responses-wrapper">
                <div class="proposals-wrapper">
                  <div class="vppa-status">
                    <div class="vppa-info">
                      ${when(
                        (x) => x.structuredChatService.negotiationInViewDetails.NEGOTIATION_STATUS,
                        html`
                          <div
                            class="status ${(x) =>
                              x.structuredChatService.negotiationInViewDetails
                                .NEGOTIATION_STATUS_CLASS}"
                          >
                            ${(x) =>
                              x.structuredChatService.negotiationInViewDetails.NEGOTIATION_STATUS}
                          </div>
                        `,
                      )}
                      <div class="company">
                        ${(x) => x.structuredChatService.negotiationInViewDetails.COMPANY_NAME}
                      </div>
                    </div>
                    <div class="actions">
                      ${when(
                        (x) =>
                          x.structuredChatService.negotiationInViewDetails.NEGOTIATION_STATUS ===
                            'Staged' && x.showLinkMore,
                        html`
                          <zero-button appearance="stealth" @click="${(x) => x.showRFQSelector()}">
                            Link more RFQs
                          </zero-button>
                        `,
                      )}
                      <zero-button
                        appearance="stealth"
                        @click="${(x) => (x.isNegotiationTermsOpened = true)}"
                        ?disabled="${(x) =>
                          x.structuredChatService.validStatusMap
                            .get(VALID_STRUCTURED_CHAT_STATUS.SUCCESSFUL)
                            .includes(
                              x.structuredChatService.negotiationInViewDetails
                                .NEGOTIATION_STATUS_ENUM_VALUE,
                            )}"
                      >
                        ${listIcon('start')}View Full Summary
                      </zero-button>
                    </div>
                  </div>
                  <div class="vppa-project-info">
                    <div class="headers">
                      <span class="header">Project</span>
                      <span class="header">Ticker</span>
                      <span class="header">Unique ID</span>
                      <span class="header">Fixed Price ($/MWh)</span>
                      <span class="header">Offer Capacity (MW)</span>
                      <span class="header">Volume (MWh/year)</span>
                      <span class="header">Tenor</span>
                      <span class="header">Post-COD Collateral</span>
                    </div>
                    ${when(
                      (x) => x.structuredChatService.negotiationInViewDetails,
                      html`
                        <div class="vppa-data">
                          <span class="hyperlink" @click=${(x) => x.openProjectPage()}>
                            ${(x) => x.structuredChatService.negotiationInViewDetails.PROJECT_NAME}
                          </span>
                          <span>
                            ${(x) => x.structuredChatService.negotiationInViewDetails.VPPA_TICKER}
                          </span>
                          <span>
                            ${(x) =>
                              x.structuredChatService.negotiationInViewDetails.VPPA_UNIQUE_ID}
                          </span>
                          <span>
                            ${(x) =>
                              x.structuredChatService.negotiationInViewDetails.VPPA_FIXED_PRICE}
                          </span>
                          <span>
                            ${(x) =>
                              getFormattedNumber(
                                x.structuredChatService.negotiationInViewDetails
                                  .VPPA_OFFERED_CAPACITY,
                              )}
                          </span>
                          <span class="calculated-values">
                            ${(x) =>
                              getFormattedNumber(
                                x.structuredChatService.negotiationInViewDetails.VPPA_VOLUME,
                              )}
                          </span>
                          <span>
                            ${(x) =>
                              getTenorRangePattern(
                                x.structuredChatService.negotiationInViewDetails
                                  .VPPA_TENOR_START_DATE,
                                x.structuredChatService.negotiationInViewDetails.VPPA_TENOR_MONTHS,
                              )}
                          </span>
                          <span>
                            ${(x) =>
                              x.structuredChatService.negotiationInViewDetails.VPPA_COLLATERAL
                                ? getFormattedNumber(
                                    x.structuredChatService.negotiationInViewDetails
                                      .VPPA_COLLATERAL,
                                  )
                                : getAbbreviationForCreditProfile(x.structuredChatService.negotiationInViewDetails.VPPA_CREDIT_PROFILE)}
                          </span>
                        </div>
                        <div class="rfq-data">
                          <span></span>
                          <span>
                            ${(x) => x.structuredChatService.negotiationInViewDetails.RFQ_TICKER}
                          </span>
                          <span>
                            ${(x) => x.structuredChatService.negotiationInViewDetails.RFQ_UNIQUE_ID}
                          </span>
                          <span>
                            ${(x) =>
                              x.structuredChatService.negotiationInViewDetails.RFQ_FIXED_PRICE}
                          </span>
                          <span class="calculated-values">
                            ${(x) =>
                              getFormattedNumber(
                                x.structuredChatService.negotiationInViewDetails
                                  .RFQ_OFFERED_CAPACITY,
                              )}
                          </span>
                          <span>
                            ${(x) =>
                              getFormattedNumber(
                                x.structuredChatService.negotiationInViewDetails.RFQ_VOLUME,
                              )}
                          </span>
                          <span>
                            ${(x) =>
                              getTenorRangePattern(
                                x.structuredChatService.negotiationInViewDetails
                                  .RFQ_TENOR_START_DATE,
                                x.structuredChatService.negotiationInViewDetails.RFQ_TENOR_MONTHS,
                              )}
                          </span>
                          <span>
                            ${(x) =>
                              x.structuredChatService.negotiationInViewDetails.RFQ_COLLATERAL
                                ? getFormattedNumber(
                                    x.structuredChatService.negotiationInViewDetails.RFQ_COLLATERAL,
                                  )
                                : getAbbreviationForCreditProfile(x.structuredChatService.negotiationInViewDetails.RFQ_CREDIT_PROFILE)
                              }
                          </span>
                        </div>
                      `,
                    )}
                  </div>
                  <div class="proposals">
                    ${repeat(
                      (x) => x.structuredChatService.allProposals,
                      html`
                        <div
                          class="chat-bubble ${(x, c) =>
                            c.parent.structuredChatService.isCompanyProposal(x)
                              ? 'current-user'
                              : ''}"
                        >
                          <div class="user-date">
                            <span>
                              ${(x) =>
                                x.PROPOSAL_SENDER === 'BUYER'
                                  ? x.BUYER_COMPANY_NAME
                                  : x.SELLER_COMPANY_NAME}
                            </span>
                            <span>${(x) => getFormattedDate(x.CREATED_AT)}</span>
                          </div>
                          <div class="proposal-details">
                            <div class="column">
                              <div>
                                <div class="header">Price</div>
                                <div
                                  class="data changed-value  ${(x, c) =>
                                    c.parent.counterOfferChanges(x.PROPOSAL_ID)
                                      ?.fixedPriceHasChanged
                                      ? 'true'
                                      : 'false'}"
                                >
                                  ${(x) => `$${x.FIXED_PRICE}/MWh`}
                                </div>
                              </div>
                              <div>
                                <div class="header">Capacity</div>
                                <div
                                  class="data changed-value ${(x, c) =>
                                    c.parent.counterOfferChanges(x.PROPOSAL_ID)
                                      ?.offeredCapacityHasChanged
                                      ? 'true'
                                      : 'false'}"
                                >
                                  ${(x) => getFormattedNumber(x.OFFERED_CAPACITY)}MW
                                </div>
                              </div>
                            </div>
                            <div class="column">
                              <span class="master-header">Tenor</span>
                              <div>
                                <div class="header">Start Date</div>
                                <div
                                  class="data changed-value ${(x, c) =>
                                    c.parent.counterOfferChanges(x.PROPOSAL_ID)
                                      ?.tenorStartDateHasChanged
                                      ? 'true'
                                      : 'false'}"
                                >
                                  ${(x) => getFormattedDate(x.TENOR_START_DATE)}
                                </div>
                              </div>
                              <div>
                                <div class="header">Months</div>
                                <div
                                  class="data changed-value ${(x, c) =>
                                    c.parent.counterOfferChanges(x.PROPOSAL_ID)
                                      ?.tenorMonthsHasChanged
                                      ? 'true'
                                      : 'false'}"
                                >
                                  ${(x) => x.TENOR_MONTHS}
                                </div>
                              </div>
                            </div>
                            <div class="column">
                              <span class="master-header">
                                Post-COD Credit
                                <br />
                                $/MW (Max)
                              </span>
                              <div>
                                <div class="header">Buyer</div>
                                <div
                                  class="data changed-value ${(x, c) =>
                                    c.parent.counterOfferChanges(x.PROPOSAL_ID)
                                      ?.buyerCODCollateralHasChanged
                                      ? 'true'
                                      : 'false'}"
                                >
                                  ${(x) =>
                                    x.BUYER_POST_COD_COLLATERAL
                                      ? getFormattedNumber(x.BUYER_POST_COD_COLLATERAL)
                                      : getAbbreviationForCreditProfile(x.RFQ_CREDIT_PROFILE)}
                                </div>
                              </div>
                              <div>
                                <div class="header">Seller</div>
                                <div
                                  class="data changed-value ${(x, c) =>
                                    c.parent.counterOfferChanges(x.PROPOSAL_ID)
                                      ?.sellerCODCollateralHasChanged
                                      ? 'true'
                                      : 'false'}"
                                >
                                  ${(x) =>
                                    x.SELLER_POST_COD_COLLATERAL
                                      ? getFormattedNumber(x.SELLER_POST_COD_COLLATERAL)
                                      : getAbbreviationForCreditProfile(x.VPPA_CREDIT_PROFILE)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      `,
                    )}
                  </div>
                  ${when(
                    (x) =>
                      (x.structuredChatService.negotiationInViewDetails &&
                        x.structuredChatService.negotiationInViewDetails.NEGOTIATION_STATUS ===
                          'Rejected') ||
                      x.structuredChatService.negotiationInViewDetails.NEGOTIATION_STATUS ===
                        'Requires Confirmation',
                    html`
                      <div class="offer-status">
                        <div
                          class="status ${(x) =>
                            x.structuredChatService.negotiationInViewDetails
                              .NEGOTIATION_STATUS_CLASS}"
                        >
                          ${(x) => x.structuredChatService.getLastProposalSenderName()}
                          ${(x) =>
                            x.structuredChatService.negotiationInViewDetails.NEGOTIATION_STATUS ===
                            'Rejected'
                              ? 'rejected the offer'
                              : 'accepted the offer'}
                        </div>
                      </div>
                    `,
                  )}
                  ${when(
                    (x) =>
                      x.structuredChatService.validStatusMap
                        .get(VALID_STRUCTURED_CHAT_STATUS.SUCCESSFUL)
                        .includes(
                          x.structuredChatService.negotiationInViewDetails
                            .NEGOTIATION_STATUS_ENUM_VALUE,
                        ),
                    html`
                      <div class="offer-status">
                        <div class="status successful">
                          ${(x) => x.structuredChatService.getLastProposalSenderName()} accepted the
                          offer
                        </div>
                      </div>
                    `,
                  )}
                  ${when(
                    (x) =>
                      x.structuredChatService.validStatusMap
                        .get(VALID_STRUCTURED_CHAT_STATUS.TRADED_AWAY)
                        .includes(
                          x.structuredChatService.negotiationInViewDetails
                            .NEGOTIATION_STATUS_ENUM_VALUE,
                        ),
                    html`
                      <div class="offer-status">
                        <div class="status rejected">
                          ${(x) =>
                            x.structuredChatService.negotiationInView.VPPA_STATUS ===
                            VPPA_RFQ_STATUS.EXECUTED
                              ? 'Contract '
                              : 'RFQ '}has
                          traded away. Negotiation has been terminated.
                        </div>
                      </div>
                    `,
                  )}
                </div>
                ${when(
                  (x) =>
                    !x.structuredChatService.negotiationInView ||
                    (x.structuredChatService.negotiationInView &&
                      (x.structuredChatService.negotiationInView.NEGOTIATION_STATUS ===
                        VALID_STRUCTURED_CHAT_STATUS.IN_PROGRESS ||
                        x.structuredChatService.negotiationInView.NEGOTIATION_STATUS ===
                          VALID_STRUCTURED_CHAT_STATUS.STAGED)),
                  html<StructuredChatModal>`
                    <section
                      class="proposal-header"
                      style="display: ${(x) =>
                        x.structuredChatService.negotiationInViewDetails.NEGOTIATION_STATUS ===
                          'Staged' && x.isCurrentUserBuyer()
                          ? ''
                          : 'none'}"
                    >
                      Refine RFQ Terms Prior to Sending Proposal
                    </section>
                    <div class="responses">
                      <div class="inputs">
                        <div class="top-inputs">
                          <zero-number-field
                            ${ref('fixedPrice')}
                            hide-step
                            :value=${sync((x) => x.fixedPriceValue)}
                            style="width: 114px"
                            placeholder="Enter..."
                            oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
                            ?disabled=${(x) =>
                              x.structuredChatService.disabledAcceptReject &&
                              x.structuredChatService.negotiationInViewDetails
                                .NEGOTIATION_STATUS !== 'Staged'}
                          >
                            Fixed Price
                          </zero-number-field>
                          <zero-number-field
                            ${ref('offeredCapacity')}
                            hide-step
                            :value=${sync((x) => x.offeredCapacityValue)}
                            placeholder="Enter..."
                            oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
                            style="width: 190px;"
                            ?disabled=${(x) =>
                              x.structuredChatService.disabledAcceptReject &&
                              x.structuredChatService.negotiationInViewDetails
                                .NEGOTIATION_STATUS !== 'Staged'}
                          >
                            Offered Capacity
                          </zero-number-field>
                          <div class="form-control date">
                            <label>Tenor Start Date</label>
                            <zero-text-field
                              type="date"
                              onkeydown="return false"
                              ${ref('tenorStartDate')}
                              :value=${sync((x) => x.tenorStartDateValue)}
                              placeholder="Enter..."
                              ?disabled=${(x) =>
                                x.structuredChatService.disabledAcceptReject &&
                                x.structuredChatService.negotiationInViewDetails
                                  .NEGOTIATION_STATUS !== 'Staged'}
                            ></zero-text-field>
                          </div>
                          <zero-number-field
                            ${ref('tenorMonths')}
                            hide-step
                            placeholder="Enter..."
                            :value=${sync((x) => x.tenorMonthsValue)}
                            oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
                            ?disabled=${(x) =>
                              x.structuredChatService.disabledAcceptReject &&
                              x.structuredChatService.negotiationInViewDetails
                                .NEGOTIATION_STATUS !== 'Staged'}
                          >
                            Tenor (Months)
                          </zero-number-field>
                        </div>
                        <div class="bottom-inputs">
                          <zero-text-field
                            ${ref('buyerCODCollateral')}
                            :value=${sync((x) => x.buyerCODCollateralValue)}
                            placeholder="Enter..."
                            ?disabled=${(x) =>
                              !(
                                x.structuredChatService.negotiationInViewDetails &&
                                x.structuredChatService.negotiationInViewDetails.RFQ_COLLATERAL
                              ) ||
                              (x.structuredChatService.disabledAcceptReject &&
                                x.structuredChatService.negotiationInViewDetails
                                  .NEGOTIATION_STATUS !== 'Staged')}
                          >
                            Buyer Post-COD Credit $/MW (Max)
                          </zero-text-field>
                          <zero-text-field
                            ${ref('sellerCODCollateral')}
                            :value=${sync((x) => x.sellerCODCollateralValue)}
                            ?disabled=${(x) =>
                              !(
                                x.structuredChatService.negotiationInViewDetails &&
                                x.structuredChatService.negotiationInViewDetails.VPPA_COLLATERAL
                              ) ||
                              (x.structuredChatService.disabledAcceptReject &&
                                x.structuredChatService.negotiationInViewDetails
                                  .NEGOTIATION_STATUS !== 'Staged')}
                            placeholder="Enter..."
                          >
                            Seller Post-COD Credit $/MW (Max)
                          </zero-text-field>
                        </div>
                      </div>
                      ${when(
                          (x) => (
                            x.structuredChatService.negotiationInView &&
                            x.isMaskingEnabled &&
                            x.structuredChatService.negotiationInView.SELLER_COMPANY_ID === x.currentCompany.CASCADE_COMPANY_ID &&
                            !x.companyUnmaskedProjects.includes(x.structuredChatService.negotiationInView.PROJECT_ID)
                          ),
                          html`
                          <div class="unmasking-warning text-red">
                            ${sync((x) => x.structuredChatService.sellerMaskingWarning())}
                          </div>`,
                          )
                      }
                      <div class="actions">
                        <zero-button
                          ?disabled=${(x) => x.structuredChatService.disabledCounter}
                          appearance="chat-counter"
                          @click="${(x) => x.sendOffer()}"
                        >
                          ${(x) =>
                            x.structuredChatService.negotiationInView &&
                            x.structuredChatService.negotiationInView.NEGOTIATION_STATUS ===
                              'STAGED'
                              ? 'Send Refined Terms'
                              : 'Counter'}
                        </zero-button>
                        <zero-button
                          ?disabled=${(x) => x.structuredChatService.disabledAcceptReject}
                          appearance="chat-accept"
                          @click="${(x) => x.acceptNegotiation()}"
                          style="display: ${(x) =>
                            x.structuredChatService.negotiationInView &&
                            x.structuredChatService.negotiationInView.NEGOTIATION_STATUS ===
                              'STAGED'
                              ? 'none'
                              : ''}"
                        >
                          Accept
                        </zero-button>
                        <zero-button
                          ?disabled=${(x) =>
                            x.structuredChatService.negotiationInView &&
                            x.structuredChatService.negotiationInView.NEGOTIATION_STATUS ===
                              'STAGED'
                              ? x.structuredChatService.disabledCounter
                              : x.structuredChatService.disabledAcceptReject}
                          appearance="chat-reject"
                          @click="${(x) =>
                            x.structuredChatService.disabledAcceptReject &&
                            !x.structuredChatService.disabledCounter
                              ? x.closeModal()
                              : x.sendReject()}"
                        >
                          ${(x) =>
                            x.structuredChatService.negotiationInView &&
                            x.structuredChatService.negotiationInView.NEGOTIATION_STATUS ===
                              'STAGED'
                              ? 'Discard Proposal'
                              : 'Reject'}
                        </zero-button>
                      </div>
                    </div>
                  `,
                )}

                  ${when(
                    (x) =>
                      x.structuredChatService.negotiationInView &&
                      (x.structuredChatService.negotiationInView.NEGOTIATION_STATUS ===
                        VALID_STRUCTURED_CHAT_STATUS.REQUIRES_CONFIRMATION ||
                        x.structuredChatService.negotiationInView.NEGOTIATION_STATUS ===
                          USED_STRUCTURED_CHAT_STATUS.CONFIRMED_AWAITING_BUYER ||
                        x.structuredChatService.negotiationInView.NEGOTIATION_STATUS ===
                          USED_STRUCTURED_CHAT_STATUS.CONFIRMED_AWAITING_SELLER ||
                        x.structuredChatService.validStatusMap
                          .get(VALID_STRUCTURED_CHAT_STATUS.SUCCESSFUL)
                          .includes(
                            x.structuredChatService.negotiationInViewDetails
                              .NEGOTIATION_STATUS_ENUM_VALUE,
                          )),
                    html`
                      <div class="actions responses">
                        <zero-button
                          appearance="chat-counter"
                          class="trade-confirmation"
                          @click="${(x) => x.toggleSummary()}"
                        >
                          Go to Trade Summary
                        </zero-button>
                      </div>
                    `,
                  )}
                  ${when(
                    (x) => x.isSummaryOpened,
                    html<StructuredChatModal>`
                      <summary-chat
                        :negotiationId="${sync(
                          (x) => x.structuredChatService.selectedNegotiationID,
                        )}"
                        :structuredChatEnums="${sync((x) => x.structuredChatEnums)}"
                      >
                        <div slot="header" class="summary-title">
                          <span class="header">Pre-trade Summary</span>
                          <span @click="${(x) => x.toggleSummary()}" class="return-back">
                            <span class="closing-icon">${closeIcon}</span>
                          </span>
                        </div>
                        <div slot="warning">
                          ${sync((x) => x.structuredChatService.warningMessage)}
                        </div>
                        <zero-button
                          appearance="danger"
                          @click="${(x) => x.returnToNegotiation()}"
                          class="with-icon"
                          slot="actions"
                          ?disabled="${(x) =>
                            !x.structuredChatService.isCompanyAllowedToReturnToNegotiation()}"
                        >
                          ${whiteErrorIcon('start')} Return to Negotiation
                        </zero-button>
                        <zero-button
                          appearance="primary"
                          class="with-icon"
                          slot="actions"
                          title="Click here to accept Pre-trade confirmation and begin contract negotiations in the contracting module."
                          ?disabled="${(x) =>
                            !x.structuredChatService.isCompanyAllowedToConfirmNegotiation()}"
                        >
                          ${whiteCheckIcon('start')} Agree
                        </zero-button>
                      </summary-chat>
                    `,
                  )}
                  ${when(
                    (x) => x.isNegotiationTermsOpened,
                    html<StructuredChatModal>`
                      <summary-chat
                        :negotiationId="${sync(
                          (x) => x.structuredChatService.selectedNegotiationID,
                        )}"
                        :structuredChatEnums="${sync((x) => x.structuredChatEnums)}"
                      >
                        <div slot="header">Summary</div>
                        <zero-button
                          appearance="default"
                          @click="${(x) => (x.isNegotiationTermsOpened = false)}"
                          slot="actions"
                        >
                          Return to Negotiation
                        </zero-button>
                      </summary-chat>
                    `,
                  )}
                </div>
                ${when(
                  (x) =>
                    x.isSummaryOpened &&
                    x.structuredChatService.validStatusMap
                      .get(VALID_STRUCTURED_CHAT_STATUS.SUCCESSFUL)
                      .includes(
                        x.structuredChatService.negotiationInViewDetails
                          .NEGOTIATION_STATUS_ENUM_VALUE,
                      ),
                  html<StructuredChatModal>`
                    <summary-chat
                      :negotiationId="${sync((x) => x.structuredChatService.selectedNegotiationID)}"
                      :structuredChatEnums="${sync((x) => x.structuredChatEnums)}"
                    >
                      <div slot="header" class="summary-title">
                        <span class="header">Pre-trade Summary</span>
                        <span @click="${(x) => x.toggleSummary()}" class="return-back">
                          <span class="closing-icon">${closeIcon}</span>
                        </span>
                      </div>
                      <div slot="warning">
                        ${sync((x) => x.structuredChatService.warningMessage)}
                      </div>
                      <zero-button
                        class="arteria-button"
                        appearance="chat-accept"
                        @click="${(x) => x.goToArteriaNegotiationsPage()}"
                        slot="actions"
                      >
                        Proceed to Contract Negotiation
                      </zero-button>
                    </summary-chat>
                  `,
                )}
                ${when(
                  (x) =>
                    x.isSummaryOpened &&
                    !x.structuredChatService.validStatusMap
                      .get(VALID_STRUCTURED_CHAT_STATUS.SUCCESSFUL)
                      .includes(
                        x.structuredChatService.negotiationInViewDetails
                          .NEGOTIATION_STATUS_ENUM_VALUE,
                      ),
                  html<StructuredChatModal>`
                    <summary-chat
                      :negotiationId="${sync((x) => x.structuredChatService.selectedNegotiationID)}"
                      :structuredChatEnums="${sync((x) => x.structuredChatEnums)}"
                    >
                      <div slot="header" class="summary-title">
                        <span class="header">Pre-trade Summary</span>
                        <span @click="${(x) => x.toggleSummary()}" class="return-back">
                          <span class="closing-icon">${closeIcon}</span>
                        </span>
                      </div>
                      <div slot="warning">
                        ${sync((x) => x.structuredChatService.warningMessage)}
                      </div>
                      <zero-button
                        appearance="danger"
                        @click="${(x) => x.returnToNegotiation()}"
                        class="with-icon"
                        slot="actions"
                        ?disabled="${(x) =>
                          !x.structuredChatService.isCompanyAllowedToReturnToNegotiation()}"
                      >
                        ${whiteErrorIcon('start')} Return to Negotiation
                      </zero-button>
                      <zero-button
                        appearance="primary"
                        @click="${(x) => x.sendConfirm()}"
                        class="with-icon"
                        slot="actions"
                        title="Click here to accept Pre-trade confirmation and begin contract negotiations in the contracting module."
                        ?disabled="${(x) =>
                          !x.structuredChatService.isCompanyAllowedToConfirmNegotiation()}"
                      >
                        ${whiteCheckIcon('start')} Agree
                      </zero-button>
                    </summary-chat>
                  `,
                )}
                ${when(
                  (x) => x.isNegotiationTermsOpened,
                  html<StructuredChatModal>`
                    <summary-chat
                      :negotiationId="${sync((x) => x.structuredChatService.selectedNegotiationID)}"
                      :structuredChatEnums="${sync((x) => x.structuredChatEnums)}"
                    >
                      <div slot="header" class="summary-title">
                        <span class="header">Summary</span>
                        <span
                          @click="${(x) => (x.isNegotiationTermsOpened = false)}"
                          class="return-back"
                        >
                          <span class="closing-icon">${closeIcon}</span>
                        </span>
                      </div>
                      <zero-button
                        appearance="default"
                        @click="${(x) => (x.isNegotiationTermsOpened = false)}"
                        slot="actions"
                      >
                        Return to Negotiation
                      </zero-button>
                    </summary-chat>
                  `,
                )}
              </div>
            `,
              )}
            </div>
          `,
        )}
      `,
    )}
  </right-side-modal>
`;
